/**
 * Creates an SSR-safe storage interface that uses the specified browser storage
 * in browser environments and falls back to in-memory storage during SSR.
 *
 * @param browserStorage - The browser storage to use (localStorage, sessionStorage, etc.)
 * @returns A storage interface with getItem, setItem, and removeItem methods
 */
export function createSSRSafeStorage(browserStorage?: Storage) {
  // SSR-safe check for browser environment
  const isBrowser = typeof window !== 'undefined';

  // Fallback storage for SSR - creates a simple in-memory storage
  function createMemoryStorage() {
    const memoryStorage = new Map<string, string>();
    return {
      getItem: (key: string) => memoryStorage.get(key) ?? null,
      setItem: (key: string, value: string) => memoryStorage.set(key, value),
      removeItem: (key: string) => memoryStorage.delete(key),
    };
  }

  // Use provided browser storage in browser, memory storage for SSR
  // Default to sessionStorage if no storage is provided
  const storage = isBrowser ? browserStorage ?? sessionStorage : createMemoryStorage();

  return storage;
}
